/**
 * An N/A cell of the quantity grids: "N/A" with an info icon whose tooltip says why the row has no
 * such value — the pattern the workspace's number cells use for a missing value.
 */
import { IconTooltip, Row, Text, TooltipContent } from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import type { ColDef, ValueFormatterParams } from "ag-grid-community";
import type { CustomCellRendererProps } from "ag-grid-react";
import type { Instrument } from "../../const";
import { withCellMarker } from "../withCellMarker";

/** Why `row` has no value in the column — the tooltip text — or nothing where it has one. */
export type NotAvailableReason = (row: Instrument) => string | undefined;

export function NotAvailableCell({
	data,
	why,
}: CustomCellRendererProps<Instrument> & { why: NotAvailableReason }): React.JSX.Element {
	const reason = data ? why(data) : undefined;
	return (
		<Row className="h-full w-full" justifyContent="end" alignItems="center" gap={4}>
			<Text type="Body/M/Book" data-qualifier="CompositionEditor/NotAvailable">
				N/A
			</Text>
			{reason && (
				<IconTooltip severity="info" overrideIconColor={themeCSSVars.palette_N700} iconSize={16} hideArrow>
					<TooltipContent>
						<span data-qualifier="CompositionEditor/NotAvailableReason">{reason}</span>
					</TooltipContent>
				</IconTooltip>
			)}
		</Row>
	);
}

/**
 * Layers the N/A cell onto a column, for the rows `why` gives a reason for: the cell reads "N/A" —
 * in an export and a copy too — and its icon says why. Every other row renders as before.
 */
export function withNotAvailableCell<T extends Instrument = Instrument>(
	column: ColDef<T>,
	why: NotAvailableReason,
): ColDef<T> {
	const { valueFormatter } = column;
	return withCellMarker(
		{
			...column,
			valueFormatter: (params: ValueFormatterParams<T>) =>
				params.data != null && why(params.data) != null
					? "N/A"
					: typeof valueFormatter === "function"
						? valueFormatter(params)
						: String(params.value ?? ""),
		},
		{ applies: (row) => why(row) != null, Marker: NotAvailableCell, params: { why } },
	);
}
